/**
 * Referral attribution (first-touch).
 *
 * Captures the `?ref=` parameter from the landing URL and persists it to
 * localStorage so it survives the signup + email-verification flow, then rides
 * along on the create_user API call and is stored against the new user.
 *
 * First-touch: we only store a value if none is stored yet, so the original
 * referrer isn't overwritten by later internal navigations.
 */

const STORAGE_KEY = 'cc_referral_source';
const MAX_LEN = 128;

/**
 * Read `?ref=` (or `?referral=`) from the current URL and persist it once.
 * Safe to call on every app load; never throws (localStorage can be blocked
 * in private mode, so we swallow errors and never block app boot).
 */
export function captureReferralFromUrl() {
  try {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref') || params.get('referral');
    if (ref && !localStorage.getItem(STORAGE_KEY)) {
      localStorage.setItem(STORAGE_KEY, String(ref).slice(0, MAX_LEN));
    }
  } catch (e) {
    /* localStorage unavailable — ignore */
  }
}

/** Return the stored referral source, or null. */
export function getReferralSource() {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch (e) {
    return null;
  }
}

/** Clear the stored referral once it has been attributed to a signup. */
export function clearReferralSource() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* ignore */
  }
}
